import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import { Button } from '../common/Button';
import ProfileAvatar from '../common/ProfileAvatar';
import RatingStars from '../common/RatingStars';
import { contactStore } from '../../stores/contactStore';
import { useProfileRole } from '../../hooks/useProfileRole';
import { getFreelancerSubtitle, getServices } from '../../utils/freelancerUtils';
import { Contact } from '../../types/contact';

import { FreelancerSearchResult } from '../../types/search';

const CONTACTS_COUNT = 2;

function ContactFreelancerCard({
  contact,
  allianceCount,
  services,
  location,
}: {
  contact: Contact;
  allianceCount: string;
  services: string;
  location: string;
}) {
  const { t } = useTranslation(['common', 'freelancers']);

  return (
    <Link
      to="/prospects"
      state={{ selected_id: contact.id }}
      className="block flex cursor-pointer items-start gap-2 rounded-lg border-b-2 border-gray-100 p-2 transition-colors last:border-b-0 hover:bg-gray-50"
    >
      <ProfileAvatar
        profile_id={contact.id}
        first_name={contact.first_name ?? ''}
        avatar_url={contact.avatar_url}
        className="h-[50px] w-[50.125px] flex-shrink-0"
      />

      <div className="flex min-w-0 flex-1 flex-col">
        <div className="mb-2 flex flex-wrap items-baseline gap-2">
          <span className="truncate text-sm font-medium text-secondary">
            {contact.first_name} {contact.last_name}
          </span>
          {services && (
            <span className="text-sm font-bold text-secondary">
              {getFreelancerSubtitle(contact, t)}
            </span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3 text-sm text-secondary">
          {location && <span className="flex items-center text-sm">{location}</span>}

          <span className="flex items-center gap-1 text-sm">
            {allianceCount} {t('alliances', { count: contact.alliance_count || 0 })}
          </span>

          <div className="flex items-center gap-1">
            <RatingStars rating={contact.overall_rating || 0} starClassName={''} />
          </div>
        </div>
      </div>
    </Link>
  );
}

function ContactRecruiterCard({ contact }: { contact: Contact }) {
  const { t } = useTranslation(['common', 'freelancers']);
  const name = contact.first_name ?? '';
  const subtitle = contact.company_name ?? '';
  const role = contact.role ?? '';

  return (
    <Link
      to="/prospects"
      state={{ selected_id: contact.id }}
      className={twMerge(
        'flex items-center gap-4 py-3 hover:cursor-pointer  border-b-2 border-gray-100 last:border-b-0 w-full'
      )}
      role="button"
      tabIndex={0}
    >
      <ProfileAvatar
        profile_id={contact.id}
        first_name={contact.first_name ?? ''}
        avatar_url={contact.avatar_url}
        className="h-[50px] w-[50.125px] flex-shrink-0"
      />

      {/* VERSION MOBILE */}
      <div className="flex min-w-0 flex-1 items-center justify-between md:hidden">
        <div className="flex min-w-0 flex-col">
          <div className="truncate text-base leading-tight">{name}</div>
          <div className="mt-1 text-xs text-[#5A6B82]">
            {role && (
              <span className="text-xs text-[#5A6B82]">{getFreelancerSubtitle(contact, t)}</span>
            )}
            <span className="text-xs text-[#5A6B82]">{subtitle}</span>
          </div>
        </div>
      </div>

      {/* VERSION DESKTOP */}
      <div className="hidden min-w-0 flex-1 items-center justify-between md:flex">
        <div className="min-w-0 flex-1 pr-4">
          <div className="truncate text-base leading-tight md:text-lg">{name}</div>
          <div className="text-xs text-[#5A6B82]">{getFreelancerSubtitle(contact, t)}</div>
        </div>

        <div className="min-w-0 flex-1 pr-4">
          <div className="truncate text-sm font-bold text-secondary">{subtitle}</div>
        </div>

        <div className="flex-shrink-0">
          <div className="flex items-center gap-4">
            <div className="flex flex-col gap-1">
              <span className="text-sm">{t('rating', { ns: 'freelancers' })}</span>
              <RatingStars rating={contact.overall_rating || 0} starClassName={''} />
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}

const ContactSection = () => {
  const { t } = useTranslation(['common', 'dashboard', 'prospects', 'freelancers']);
  const { contacts, loading, fetchContacts } = contactStore() as {
    contacts: Contact[];
    loading: boolean;
    fetchContacts: () => void;
  };
  const { isFreelancer } = useProfileRole();

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  const getAllianceCount = (allianceCount: number): string => {
    if (allianceCount > 10) return '10+';
    if (allianceCount === 0) return '0';
    return String(allianceCount);
  };

  const getLocation = (cityname: string): string => {
    const city = cityname;
    return city ? `${city}` : '';
  };

  if (loading) {
    return (
      <div className="flex flex-col gap-2 rounded-2xl border-2 border-gray-300 bg-white p-6 shadow-xl">
        <h2 className="mb-4 text-[1.2rem] text-lg font-semibold text-secondary">
          {t('dashboard:my_contacts')}
        </h2>
        <div className="text-sm text-secondary">{t('common:loading')}</div>
      </div>
    );
  }
  const contactsToShow = contacts.slice(0, CONTACTS_COUNT);

  return (
    <div className="flex flex-col gap-2 rounded-2xl border-2 border-gray-300 bg-white p-6 shadow-xl">
      <div className="mb-4 flex items-center justify-between">
        <div className="relative inline-block">
          <h2 className="inline-block text-xl font-semibold text-secondary">
            {t('dashboard:my_contacts')}
          </h2>
        </div>

        <Button
          variant="transparent-with-border"
          href="/prospects"
          className="hidden text-sm md:flex"
        >
          {t('view_all_contacts', { ns: 'contact' })}
        </Button>
      </div>

      {!contactsToShow || contactsToShow.length === 0 ? (
        <div className="mb-4 text-sm text-secondary">{t('prospects:noContacts')}</div>
      ) : (
        contactsToShow.map(contact => {
          const allianceCount = getAllianceCount(contact.alliance_count || 0);
          const services = getServices(contact as unknown as FreelancerSearchResult);
          const location = getLocation(contact.cityname ?? '');

          return isFreelancer ? (
            <ContactRecruiterCard key={contact.id} contact={contact} />
          ) : (
            <ContactFreelancerCard
              key={contact.id}
              contact={contact}
              allianceCount={allianceCount}
              services={services}
              location={location}
            />
          );
        })
      )}
    </div>
  );
};

export default ContactSection;
